 
import { useAuth } from 'helpers/authContext';
import { commentCreateWithWif } from 'helpers/steem';
import React, { useState } from 'react'; 
import { Button, Label } from 'reactstrap';
import { fetchReplies } from './repliesSlice';
import { useDispatch } from 'react-redux';
import { isLoggedWithKeychain } from 'services/dsteem-service';
import { commentCreateKeychain } from 'helpers/keychain'; 
import { useForm } from 'react-hook-form';
import FullEditor from 'components/FullEditor';
import { clearUploadState, getUploadState } from 'helpers/UploadState';
import { cancelUpload } from 'services/db-service';
import { uploadManager } from 'helpers/UploadContext';
import { removeVideo } from 'helpers/UploadDB';
import renderer, { replaceIPFSVideoLinks } from 'helpers/contentRenderer';
import { renderHTMLSafe } from 'services/posts-service';

const ReplyEditor = ({
    depth,
    author,
    permlink,
    parent_author,
    parent_permlink,
    onNewError,
    toggleReplyEditor = null,
    id = null,
    type = "full",
    onSuccess = null,
    onLogin,
    IpfsAccount
}) => { 
    const dispatch = useDispatch();
    const { user, autologin } = useAuth(); 
    let keychainLogin = isLoggedWithKeychain(autologin); 
    const [isLoadingComment, setIsLoadingComment] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [cancelUploadState, setCancelUploadState] = useState(false);
    const [paused, setPaused] = useState(false);
    const [editorKey, setEditorKey] = useState(0);
    const { register, handleSubmit, setValue, watch, reset } = useForm({
        defaultValues: {
            description: ""
        }
    });
    const handleReset = ( ) => { 
        reset({  
            description: '' 
        });

        setEditorKey(k => k + 1); 
        // setOrientation("horizontal"); 
        setIsUploading(false);
        setPaused(false); 
    };
    const handlePauseUpload = ( ) => { 
        if(isUploading){
            if(!window.confirm("Cancel current upload?")){
                return;
            }
        }  
        const state = getUploadState(); 
        if(state){
            console.log("Cancelling upload"); 
            clearUploadState(); 
            cancelUpload(state.uploadId);  
            uploadManager.remove(state.uploadId);
            setCancelUploadState(true); 
            setIsUploading(false);
            setPaused(false);
            removeVideo(state.uploadId);
        }  
    }; 
    const onSubmit = async (data) => {
        if (isUploading) {
            onNewError("Please wait until the upload finishes.");
            return;
        }
        if (!user) {
            onLogin();
            return;
        }

        const commentBody = data.description;
        if (!commentBody.trim()) {
            onNewError('Please enter a comment.');
            return;
        }

        setIsLoadingComment(true);

        const postData = {
            body: commentBody,
            parent_author: author,
            parent_permlink: permlink,
        };

        let result; 
        if (!keychainLogin) {
            result = await commentCreateWithWif(postData, autologin); 
        } else {
            result = await commentCreateKeychain(postData, user);
        } 

        if (result.success) {
            if (toggleReplyEditor && id != null) {
                toggleReplyEditor(id);
            } else {
                reset();
            }

            if (onSuccess) {
                onSuccess(result.reply || {}); 
            } else {
                dispatch(fetchReplies(
                    parent_author === '' ? author : parent_author,
                    parent_permlink === '' ? permlink : parent_permlink
                ));
            }
        } else {
            onNewError('Failed to create comment. Please try again.');
        }

        setIsLoadingComment(false);
    };
    
    let depthClass = `depth-${depth}`;

    let previewBody = null;
    try {
        let description = watch("description")?.trim();
        if (description) {
            previewBody = (
                <div className="reply-body body-content mt-2">
                    {renderHTMLSafe(replaceIPFSVideoLinks(renderer.render(description)))}
                </div>
            );
        }
    } catch (error) {
        previewBody = <div className="text-danger mt-2">Error rendering preview.</div>;
    }

    const renderPreview = type !== "simple" && previewBody && (
        <div className="reply-preview-wrapper mt-4 mb-2 pt-3 border-top">
            <Label className="text-muted small mb-2">Comment Preview:</Label>
            <div className={`reply-container depth-1 opacity-75`}>
                <div className="reply-header">
                    <img 
                        src={`https://steemitimages.com/u/${user}/avatar`} 
                        alt={user} 
                        width={30} 
                        height={30} 
                        style={{ borderRadius: '50%', marginRight: '10px' }} 
                    />
                    <span className="reply-author-name">@{user}</span> · <span className="reply-created text-muted">Just now</span>
                </div>
                {previewBody}
            </div>
        </div>
    );
    return (
        <div className={`reply-editor${type == "simple"?"-video":""} ${depthClass}`}>
            <form onSubmit={handleSubmit(onSubmit)}>
                {type == "full" && (
                    <>
                    <FullEditor
                        key={editorKey}
                        register={register} 
                        setValue={setValue}
                        watch={watch}
                        IpfsAccount={IpfsAccount}
                        placeholder='Tell something...'
                        type='comment-input'
                        cancelUploadState={cancelUploadState}
                        uploading={isUploading}
                        setUploading={setIsUploading}
                        setPaused={setPaused}
                        paused={paused}
                    />
                    <Button
                        color="default mr-2 btn-speem" 
                        type="submit" 
                        style={{width:"107px"}}
                        disabled={isLoadingComment || isUploading}
                    >
                        {isLoadingComment ? <span className="spinner" /> : "Comment"} 
                    </Button> 
                    {!isUploading && <Button  color="secondary" onClick={handleReset}><i className="fas fa-trash" /> Clear</Button>}
                    {isUploading && <Button  color="secondary" onClick={handlePauseUpload}><i className="fas fa-stop" /> Stop</Button>}
                    </>
                )}
                {type == "simple" && (
                    <div className="reply-editor-simple-wrap">
                        <textarea
                            className="form-control fancy-input"
                            placeholder="Write a comment..."
                            aria-label="Write a comment"
                            rows={2}
                            {...register("description", { required: true })}
                            onChange={(e) => setValue("description", e.target.value)}
                            value={watch("description")}
                        />
                        <button
                            className="swipe-btn-comment"
                            aria-label="Send comment" title="Send comment"
                            type="submit"
                            disabled={isLoadingComment || !watch("description")?.trim()}
                            style={{
                                border: "none",
                                flexShrink: 0
                            }}
                        >
                            {isLoadingComment ? (
                                <span className="spinner m-0" />
                            ) : (
                                <img src="/assets/img/play.png" alt="" aria-hidden="true" />
                            )}
                        </button>
                    </div>
                )}
                {renderPreview}
            </form>
        </div>
    )
};

export default ReplyEditor; 

